import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrmCommentsDto, type CrmCommentTarget } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useId, useState } from 'react';
import { api } from '../../../app/api-client';
import { useAuth } from '../../auth/auth-context';
import { formatDateTime } from '../../users/lib/format';

export function CrmCommentsSection({ target }: { target: CrmCommentTarget }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const searchParams =
    'partyId' in target && target.partyId
      ? { partyId: target.partyId }
      : target.userId
        ? { userId: target.userId }
        : 'manyChatContactId' in target && target.manyChatContactId
          ? { manyChatContactId: target.manyChatContactId }
          : 'manualLeadId' in target && target.manualLeadId
            ? { manualLeadId: target.manualLeadId }
            : { eventId: target.eventId! };
  const query = useQuery({
    queryKey: ['admin', 'crm', 'comments', target],
    queryFn: () => api.get('admin/crm/comments', { searchParams }).json().then(CrmCommentsDto.parse),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'comments'] });
  };
  const create = useMutation({
    mutationFn: (text: string) => api.post('admin/crm/comments', { searchParams, json: { text } }),
    onSuccess: () => {
      setDraft('');
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.patch(`admin/crm/comments/${id}`, { searchParams, json: { text } }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`admin/crm/comments/${id}`, { searchParams }),
    onSuccess: refresh,
  });
  return (
    <section className="space-y-3 border-t border-border/60 pt-4" aria-label="Комментарии команды">
      <h3 className="font-medium">Комментарии команды</h3>
      <p className="text-sm text-muted-foreground">Видны только нашей команде.</p>
      <label htmlFor={fieldId} className="sr-only">
        Написать комментарий
      </label>
      <textarea
        id={fieldId}
        rows={3}
        maxLength={4000}
        placeholder="Написать комментарий…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full rounded-md border border-input bg-background p-3 text-base"
      />
      <Button disabled={!draft.trim() || create.isPending} onClick={() => create.mutate(draft.trim())}>
        {create.isPending ? 'Сохранение…' : 'Добавить'}
      </Button>
      {create.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Не удалось сохранить комментарий. Текст остался в поле — попробуйте ещё раз.
        </p>
      ) : null}
      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Загрузка комментариев…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Не удалось загрузить комментарии.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Повторить
          </Button>
        </div>
      ) : query.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Комментариев пока нет.</p>
      ) : (
        <ul className="space-y-4">
          {query.data.map((comment) => (
            <li key={comment.id} className="space-y-2 border-b border-border/60 pb-3">
              <p className="text-sm text-muted-foreground">
                {comment.author.firstName ||
                  (comment.author.username ? `@${comment.author.username}` : 'Сотрудник')}{' '}
                · {formatDateTime(comment.createdAt)}
              </p>
              {editing === comment.id ? (
                <div className="space-y-2">
                  <textarea
                    aria-label="Текст комментария"
                    className="w-full rounded-md border border-input bg-background p-3"
                    rows={3}
                    maxLength={4000}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      disabled={!editText.trim() || update.isPending}
                      onClick={() => update.mutate({ id: comment.id, text: editText.trim() })}
                    >
                      Сохранить
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(null)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap break-words text-base">{comment.text}</p>
              )}
              {(auth.adminRoles.includes('SUPERADMIN') || auth.user?.id === comment.author.id) &&
              editing !== comment.id ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditing(comment.id);
                      setEditText(comment.text);
                    }}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm('Удалить комментарий?')) remove.mutate(comment.id);
                    }}
                  >
                    Удалить
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {update.isError || remove.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Не удалось изменить комментарий. Попробуйте ещё раз.
        </p>
      ) : null}
    </section>
  );
}
